import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import { businessProfilesApi } from './api/business-profiles';
import AffiliateProgramPage from './pages/AffiliateProgramPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TracksPage from './pages/TracksPage';
import TrackDetailPage from './pages/TrackDetailPage';
import LikedTracksPage from './pages/LikedTracksPage';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import ProfilePage from './pages/ProfilePage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyEmailCallbackPage from './pages/VerifyEmailCallbackPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CertificateViewPage from './pages/CertificateViewPage';
import VerifyCertificatePage from './pages/VerifyCertificatePage';
import TVAuthPage from './pages/TVAuthPage';
import TvAppPage from './pages/tv/TvAppPage';
import TvRemotePage from './pages/tv/TvRemotePage';
import AffiliateApplyPage from './pages/AffiliateApplyPage';
import AffiliateDashboardPage from './pages/AffiliateDashboardPage';
import AdminAffiliatesPage from './pages/AdminAffiliatesPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminVisitsPage from './pages/AdminVisitsPage';
import FeaturesPage from './pages/FeaturesPage';
import RadioHomePage from './pages/RadioHomePage';
import { useAuthStore } from './store/authStore';
import { useEffect } from 'react';
import { useReferralCodeCapture } from './hooks/useReferralCode';
import { useSubscriptionEvents } from './hooks/useSubscriptionEvents';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isInitialized = useAuthStore((state) => state.isInitialized);

  // Wait for auth to initialize before redirecting
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

// Email Verification Guard - redirects unverified users to verification page
function EmailVerificationGuard({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  // If user exists and email is not verified, redirect to verification page
  if (user && !user.emailVerifiedAt) {
    return <Navigate to="/verify-email" />;
  }
  return <>{children}</>;
}

// Admin Route Guard - only allows users with admin role
function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isInitialized = useAuthStore((state) => state.isInitialized);

  // Wait for auth to initialize before redirecting
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (!user?.roles?.includes('admin')) {
    return <Navigate to="/home" />;
  }
  return <>{children}</>;
}

// /home is no longer a dashboard — radio-first work made it redundant.
// Legacy callers (login, register, verify-email, subscription-success,
// pricing, TV remote, AdminRoute non-admin bounce, header logo link)
// all still navigate('/home'); this redirect routes them into the
// SPA's radio-first homepage by picking the first available business
// profile. We can't redirect to '/' because nginx serves the Next.js
// landing there in prod — /r/<slug> is the only SPA-radio path that
// works in both dev and prod.
function HomeRedirect() {
  const { data: profiles, isLoading } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => businessProfilesApi.getAll(),
  });
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (!profiles?.length) {
    // Defensive: no profiles configured. Fall through to login so the
    // user has *somewhere* to land instead of a blank screen.
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={`/r/${profiles[0].slug}`} replace />;
}

function LegacyProfileRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/r/${slug ?? ''}`} replace />;
}

function LegacyRadioRedirect() {
  const { profileSlug, mood } = useParams<{
    profileSlug: string;
    mood: string;
  }>();
  return (
    <Navigate
      to={`/r/${profileSlug ?? ''}/${encodeURIComponent(mood ?? '')}`}
      replace
    />
  );
}

// Inner component that runs inside BrowserRouter
function AppRoutes() {
  const fetchCurrentUser = useAuthStore((state) => state.fetchCurrentUser);
  const token = useAuthStore((state) => state.token);

  // Capture referral code from URL parameter (?ref=CODE)
  useReferralCodeCapture();

  // Subscribe to real-time subscription updates via SSE
  useSubscriptionEvents();

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    }
  }, [token, fetchCurrentUser]);

  return (
    <>
      <ScrollToTop />
      <Routes>
          {/* Legacy redirects — keep deep links working. Top-level so
              they don't briefly mount the Layout before redirecting. */}
          <Route path="/profiles" element={<Navigate to="/" replace />} />
          <Route path="/profiles/:slug" element={<LegacyProfileRedirect />} />
          <Route path="/radio/:profileSlug/:mood" element={<LegacyRadioRedirect />} />

          {/* Radio-first homepage (was LandingPage / RootPage) —
              wrapped in Layout so guests + paid users get the header
              (login/profile menu) and footer. */}
          <Route element={<Layout />}>
            <Route path="/" element={<RadioHomePage />} />
            <Route path="/r/:slug" element={<RadioHomePage />} />
            <Route path="/r/:slug/:mood" element={<RadioHomePage />} />
          </Route>

          <Route path="/affiliate" element={<AffiliateProgramPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/verify/:code" element={<VerifyCertificatePage />} />
          <Route path="/certificate/view/:code" element={<CertificateViewPage />} />

          {/* TV Auth page (for phone to enter code) */}
          <Route path="/tv" element={<TVAuthPage />} />
          {/* TV App (full TV interface for smart TVs) */}
          <Route path="/tv/app" element={<TvAppPage />} />
          {/* TV Remote (control TV from phone) */}
          <Route path="/tv/remote" element={<TvRemotePage />} />
          <Route path="/tv/remote/:sessionCode" element={<TvRemotePage />} />

          {/* Auth pages */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Email verification pages */}
          <Route path="/verify-email" element={<ProtectedRoute><VerifyEmailPage /></ProtectedRoute>} />
          <Route path="/verify-email/callback" element={<VerifyEmailCallbackPage />} />

          {/* Subscription pages */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />

          {/* Protected app routes with Layout - require verified email */}
          <Route element={<Layout />}>
            <Route path="/home" element={<HomeRedirect />} />
            <Route path="/features" element={<ProtectedRoute><FeaturesPage /></ProtectedRoute>} />
            <Route path="/affiliate/apply" element={<ProtectedRoute><AffiliateApplyPage /></ProtectedRoute>} />
            <Route path="/affiliate/dashboard" element={<ProtectedRoute><AffiliateDashboardPage /></ProtectedRoute>} />
            <Route path="/admin/affiliates" element={<AdminRoute><AdminAffiliatesPage /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
            <Route path="/admin/visits" element={<AdminRoute><AdminVisitsPage /></AdminRoute>} />
            <Route path="/profile" element={<ProtectedRoute><EmailVerificationGuard><ProfilePage /></EmailVerificationGuard></ProtectedRoute>} />
            <Route path="/tracks" element={<ProtectedRoute><EmailVerificationGuard><TracksPage /></EmailVerificationGuard></ProtectedRoute>} />
            <Route path="/tracks/:id" element={<ProtectedRoute><EmailVerificationGuard><TrackDetailPage /></EmailVerificationGuard></ProtectedRoute>} />
            <Route path="/liked" element={<ProtectedRoute><EmailVerificationGuard><LikedTracksPage /></EmailVerificationGuard></ProtectedRoute>} />
          </Route>
        </Routes>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
